import React, { useState } from 'react';
import { Box, Typography, MenuItem, Select, FormControl } from '@mui/material';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import { ColombiaProductCard } from './ColombiaProductCard';

/**
 * ColombiaMenuRenderer — World Cup 2026 Colombia theme.
 *
 * DESIGN RULES:
 * - Ignores ALL styles from menuConfig / JSON.
 * - Background uses the "sticky bg" trick: a sticky element with height:0 + marginBottom pulls
 *   the rest of the content up, creating a parallax-like effect that works INSIDE any scroll
 *   container (no `position:fixed` which would bleed outside the preview box).
 */
export const WorldCupRenderer = ({ sections, theme, logoUrl, restaurantName, onProductSelect }) => {
    const [selectedSection, setSelectedSection] = useState("Todo el menú");
    const sectionRefs = {};

    const scrollToSection = (sectionName) => {
        setSelectedSection(sectionName);
        if (sectionName === "Todo el menú") {
            // try the preview scroll container first, then fall back to window
            const container = document.getElementById('preview-scroll-container');
            if (container) container.scrollTo({ top: 0, behavior: 'smooth' });
            else window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        const el = sectionRefs[sectionName];
        if (el) {
            const offset = 80;
            const container = document.getElementById('preview-scroll-container');
            if (container) {
                container.scrollTo({ top: el.offsetTop - offset, behavior: 'smooth' });
            } else {
                const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
                window.scrollTo({ top, behavior: 'smooth' });
            }
        }
    };

    return (
        /* Outer wrapper — must be position:relative so the absolute bg is clipped by
           the parent's overflow:hidden.  */
        <Box sx={{
            position: 'relative',
            fontFamily: "'Outfit', 'Roboto', sans-serif",
            /* The background lives here as a CSS property so it's clipped correctly */
            backgroundImage: `url('${theme.bgStadium}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            backgroundRepeat: 'no-repeat',
            /* backgroundAttachment:'fixed' bleeds outside containers — we avoid it.
               Instead we rely on the wrapper's overflow:hidden.              */
        }}>
            {/* Gradient overlay — darkens the stadium so text is legible */}
            <Box sx={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.58) 0%, rgba(0,5,30,0.80) 100%)',
                zIndex: 0,
                pointerEvents: 'none',
            }} />

            {/* Flag brush — top-left */}
            <Box
                component="img"
                src={theme.brushTopLeft}
                alt=""
                sx={{
                    position: 'absolute',
                    top: -8, left: -8,
                    width: '140px',
                    opacity: 0.80,
                    transform: 'scaleX(-1) scaleY(-1)',
                    zIndex: 2,
                    pointerEvents: 'none',
                    filter: 'contrast(1.1)',
                }}
            />


            {/* ──────────────────────── SCROLLABLE CONTENT ──────────────────────── */}
            <Box sx={{
                position: 'relative',
                zIndex: 5,
                maxWidth: 640,
                mx: 'auto',
                width: '100%',
            }}>

                {/* ── HEADER ── */}
                <Box sx={{
                    pt: 5, pb: 3,
                    textAlign: 'center',
                    px: 2,
                }}>
                    {logoUrl && (
                        <Box
                            component="img"
                            src={logoUrl}
                            alt="Logo"
                            sx={{
                                width: 80, height: 80,
                                borderRadius: '50%',
                                objectFit: 'cover',
                                border: `4px solid ${theme.yellow}`,
                                boxShadow: `0 0 0 3px ${theme.blue}, 0 8px 30px rgba(0,0,0,0.7)`,
                                mb: 1.5,
                                display: 'block',
                                mx: 'auto',
                            }}
                        />
                    )}

                    <Typography sx={{
                        fontWeight: 900,
                        fontSize: '1.9rem',
                        color: '#FFFFFF',
                        textTransform: 'uppercase',
                        letterSpacing: '4px',
                        lineHeight: 1,
                        textShadow: `0 0 30px rgba(0,0,0,0.9), 3px 3px 0 ${theme.blue}`,
                        mb: 1,
                    }}>
                        {restaurantName || 'MENÚ'} 🇨🇴
                    </Typography>

                    <Box sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.8,
                        bgcolor: theme.yellow,
                        color: theme.blue,
                        px: 2, py: 0.5,
                        borderRadius: '24px',
                        fontWeight: 900,
                        fontSize: '0.78rem',
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        boxShadow: `0 4px 16px rgba(252,209,22,0.5)`,
                    }}>
                        <SportsSoccerIcon sx={{ fontSize: '0.9rem' }} />
                        FIFA WORLD CUP 2026
                    </Box>
                </Box>

                {/* ── SECTION SELECTOR ── */}
                <Box sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 50,
                    px: 2,
                    pt: 'calc(36px + env(safe-area-inset-top, 0px))',
                    pb: '14px',
                    background: 'linear-gradient(to bottom, rgba(0,8,45,0.97) 0%, rgba(0,8,45,0.88) 100%)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    borderBottom: `3px solid ${theme.yellow}`,
                }}>
                    <FormControl fullWidth size="small">
                        <Select
                            value={selectedSection}
                            onChange={(e) => scrollToSection(e.target.value)}
                            displayEmpty
                            IconComponent={(p) => (
                                <ExpandMoreIcon {...p} sx={{ color: `${theme.yellow} !important` }} />
                            )}
                            sx={{
                                bgcolor: 'transparent',
                                color: '#FFF',
                                fontWeight: 900,
                                fontSize: '0.9rem',
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                borderRadius: '10px',
                                border: `2px solid ${theme.yellow}`,
                                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                '& .MuiSelect-select': { py: 1, px: 1.5 },
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        bgcolor: 'rgba(0,8,45,0.98)',
                                        backdropFilter: 'blur(12px)',
                                        borderRadius: '12px',
                                        mt: 0.5,
                                        border: `2px solid ${theme.yellow}`,
                                        '& .MuiMenuItem-root': {
                                            color: '#FFF',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            fontSize: '0.85rem',
                                            '&:hover': { bgcolor: theme.yellow, color: theme.blue },
                                            '&.Mui-selected': { bgcolor: `${theme.yellow}33`, color: theme.yellow },
                                        },
                                    }
                                }
                            }}
                        >
                            <MenuItem value="Todo el menú">🏟️ Todo el menú</MenuItem>
                            {sections.map((s) => (
                                <MenuItem key={s.name} value={s.name}>
                                    ⚽ {s.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* ── MENU SECTIONS ── */}
                <Box sx={{ pt: 2.5, pb: 10, px: 1.5 }}>
                    {sections.map((section, idx) => (
                        <Box
                            key={idx}
                            ref={(el) => { sectionRefs[section.name] = el; }}
                            sx={{ mb: 4 }}
                        >
                            {/* Section header */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.2,
                                mb: 1.5,
                                px: 1.5, py: 1,
                                borderRadius: '12px',
                                background: `linear-gradient(90deg, ${theme.blue}f0 0%, ${theme.blue}99 80%, transparent 100%)`,
                                borderLeft: `5px solid ${theme.yellow}`,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                            }}>
                                <SportsSoccerIcon sx={{ color: theme.yellow, fontSize: 22, flexShrink: 0 }} />
                                <Typography sx={{
                                    fontWeight: 900,
                                    fontSize: '1.05rem',
                                    color: '#FFFFFF',
                                    textTransform: 'uppercase',
                                    letterSpacing: '2px',
                                }}>
                                    {section.name}
                                </Typography>
                            </Box>

                            {/* Products */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {section.products.map((product) => (
                                    <ColombiaProductCard
                                        key={product.product_id || product.id}
                                        product={product}
                                        theme={theme}
                                        onSelect={onProductSelect}
                                    />
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};
